// useProgress.ts - load the progress dashboard + session debrief (S25). The dashboard comes from
// GET /api/progress; the debrief from GET /api/progress/debrief/:sessionId?cadence= (S15 routes,
// Access-identity gated in the Worker). When the Worker is unreachable (headless preview, offline, or
// ?seed=1) we fall back to the SEEDED read (seed.ts) so the surface always renders real engine values.
import { useEffect, useState } from 'react';
import type { ProgressDashboard, SeriesDebrief, CoachingCadence } from '@postflop/schema';
import { SEED_DASHBOARD, SEED_DEBRIEF } from './seed';

export interface ProgressData {
  dashboard: ProgressDashboard;
  debrief: SeriesDebrief;
  /** true when the values came from the seeded fallback rather than a live Worker fetch. */
  seeded: boolean;
  loading: boolean;
}

function forceSeed(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('seed') === '1';
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`${url} -> ${res.status}`);
  return (await res.json()) as T;
}

/**
 * Load progress for the current user + optional session. Returns the seeded read immediately when forced,
 * otherwise attempts the live endpoints and falls back to the seed on any failure. `cadence` re-fetches
 * only the debrief (the dashboard is cadence-independent).
 */
export function useProgress(sessionId: string | null, cadence: CoachingCadence): ProgressData {
  const [dashboard, setDashboard] = useState<ProgressDashboard>(SEED_DASHBOARD);
  const [debrief, setDebrief] = useState<SeriesDebrief>(SEED_DEBRIEF);
  const [seeded, setSeeded] = useState(true);
  const [loading, setLoading] = useState(!forceSeed());

  useEffect(() => {
    if (forceSeed()) {
      setDashboard(SEED_DASHBOARD);
      setDebrief(SEED_DEBRIEF);
      setSeeded(true);
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    (async () => {
      try {
        const dash = await getJson<ProgressDashboard>('/api/progress');
        const deb = sessionId
          ? await getJson<SeriesDebrief>(`/api/progress/debrief/${sessionId}?cadence=${cadence}`)
          : SEED_DEBRIEF;
        if (!live) return;
        setDashboard(dash);
        setDebrief(deb);
        setSeeded(!sessionId); // no session => debrief is still the seed
      } catch {
        if (!live) return;
        setDashboard(SEED_DASHBOARD);
        setDebrief(SEED_DEBRIEF);
        setSeeded(true);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [sessionId, cadence]);

  return { dashboard, debrief, seeded, loading };
}
